/**
 * EvidenceGridModule — Cycle-109 v2 sprint-204 S2.8.
 *
 * Canvas-mounted tile for `echelon.evidence`. Renders evidence rows for the
 * focused investigation; empty state when no investigation is in render-ctx.
 *
 * Canonical reference: `output/design_reference/design_handoff_echelon_2026_04_26/
 * workspace/modules.jsx:204-220` (EvidenceModule).
 *
 * Scope (S2.8):
 *   - TabHeader at top (sprint-1 D-1.3 primitive); no per-component header geometry.
 *   - Rose-chip styling for `kind === 'counter'` evidence chips (FR-7.7 prepwork).
 *   - Read-only — submit/redact paths live in InvestigationDrawer (sprint-3).
 *   - `inv` is consumed via the registry's `render(ctx)` adapter; ctx wiring
 *     from the canvas (focused-investigation flow) lands in sprint-3 S3.6.
 *     Until then the canvas-call path renders the empty state.
 *
 * Data path:
 *   render(ctx) → ctx.inv?: string → useEvidence(investigationId) →
 *   EvidenceEnvelopeManifest | null → rows.
 *
 * Polarity mapping (production EpistemicStatus → canonical kind):
 *   'corroborated' | 'inferred' → 'supporting'
 *   'disputed'    | 'detached'  → 'counter'
 *   undefined                   → 'supporting' (default tone — neutral safety)
 */

import { useEffect, useMemo } from 'react';
import { NotchedPanel } from '../../ui/NotchedPanel';
import { TabHeader } from '../../common/TabHeader';
import { useEvidence } from '../../../hooks/useInvestigation';
import type { EvidenceItem, EpistemicStatus } from '../../../types/investigation';

export type EvidenceChipKind = 'supporting' | 'counter';

export interface EvidenceGridModuleProps {
  /**
   * Focused investigation id from registry `render(ctx)`. When `null`/`undefined`,
   * the module renders the canonical empty state (no fetch issued).
   */
  investigationId?: string | null;
  testId?: string;
}

/**
 * Map production `EpistemicStatus` to canonical chip kind. Sprint-3 FR-7.7
 * promotion gating consumes the `counter` kind directly.
 */
export function evidenceKindForStatus(
  status: EpistemicStatus | undefined,
): EvidenceChipKind {
  if (status === 'disputed' || status === 'detached') return 'counter';
  return 'supporting';
}

/**
 * Rose chip styling for counter evidence — FR-7.7 prepwork.
 *
 * Exported for sprint-3 reuse (`InvestigationDrawer` action footer flags
 * counter evidence inline; the visual taxonomy must match the canvas tile).
 */
export function evidenceChipClasses(kind: EvidenceChipKind): string {
  if (kind === 'counter') {
    return 'border border-[var(--cd-sig-rose,#ff5d8f)]/60 bg-[var(--cd-sig-rose,#ff5d8f)]/10 text-[var(--cd-sig-rose,#ff5d8f)]';
  }
  return 'border border-[var(--e-border-secondary)]/40 bg-[var(--e-bg-elevated)]/40 text-[var(--e-text-muted)]';
}

interface EvidenceChipProps {
  kind: EvidenceChipKind;
  children: React.ReactNode;
  testId?: string;
}

/** Small kind-tagged chip — `kind === 'counter'` renders in rose. */
export function EvidenceChip({ kind, children, testId }: EvidenceChipProps) {
  return (
    <span
      data-testid={testId}
      data-kind={kind}
      className={`inline-flex items-center px-1.5 py-0.5 text-[8px] font-mono uppercase tracking-widest rounded-sm ${evidenceChipClasses(
        kind,
      )}`}
    >
      {children}
    </span>
  );
}

interface EvidenceRowProps {
  item: EvidenceItem;
  testId?: string;
}

function EvidenceRow({ item, testId }: EvidenceRowProps) {
  const kind = evidenceKindForStatus(item.epistemic_status);
  return (
    <div
      data-testid={testId}
      data-kind={kind}
      className="flex items-center gap-2 border-b border-[var(--e-border-secondary)]/20 px-2 py-1.5 text-xs last:border-b-0"
    >
      <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--e-text-muted)]/70 shrink-0">
        {item.evidence_id.slice(0, 10)}
      </span>
      <span className="flex-1 truncate text-[var(--e-text-secondary)]">
        {item.source_description}
      </span>
      <EvidenceChip kind={kind} testId={testId ? `${testId}-kind` : undefined}>
        {kind}
      </EvidenceChip>
    </div>
  );
}

const DEFAULT_TEST_ID = 'evidence-grid-module';

export function EvidenceGridModule({
  investigationId = null,
  testId = DEFAULT_TEST_ID,
}: EvidenceGridModuleProps = {}) {
  const { evidence, isLoading, error } = useEvidence(investigationId);

  // Surface fetch errors at the console so dev/ops triage isn't blind to
  // why "Evidence feed unavailable" renders. Sprint-204 review N4.
  useEffect(() => {
    if (error) console.error('[EvidenceGridModule] fetch failed:', error);
  }, [error]);

  const items = useMemo<EvidenceItem[]>(
    () => evidence?.items ?? [],
    [evidence],
  );

  return (
    <NotchedPanel
      corners={['tl', 'br']}
      notch={14}
      ticks
      accent="var(--cd-sig-iris)"
      role="region"
      aria-label="Evidence Grid"
      testId={testId}
      className="relative flex h-full min-h-[180px] flex-col gap-1 px-2 pb-3 pt-9"
    >
      <TabHeader
        label="Evidence"
        dot="var(--cd-sig-iris)"
        right={
          <span data-testid={`${testId}-count`}>
            {investigationId ? `${items.length} item${items.length === 1 ? '' : 's'}` : '—'}
          </span>
        }
      />

      {!investigationId ? (
        <div
          data-testid={`${testId}-empty-no-inv`}
          className="flex flex-1 items-center justify-center px-3 text-xs text-[var(--e-text-muted)]"
        >
          Focus an investigation to surface its evidence — select from the investigation rail.
        </div>
      ) : isLoading ? (
        <div
          data-testid={`${testId}-loading`}
          role="status"
          aria-live="polite"
          className="flex flex-1 items-center justify-center text-xs text-[var(--e-text-muted)]"
        >
          Loading evidence…
        </div>
      ) : error ? (
        <div
          data-testid={`${testId}-error`}
          role="alert"
          className="flex flex-1 items-center justify-center px-3 text-xs text-[var(--e-text-muted)]"
        >
          Evidence feed unavailable.
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid={`${testId}-empty`}
          className="flex flex-1 items-center justify-center px-3 text-xs text-[var(--e-text-muted)]"
        >
          No evidence attached yet.
        </div>
      ) : (
        <div
          data-testid={`${testId}-list`}
          className="flex flex-1 flex-col overflow-auto"
        >
          {items.map((item) => (
            <EvidenceRow
              key={item.evidence_id}
              item={item}
              testId={`${testId}-row-${item.evidence_id}`}
            />
          ))}
        </div>
      )}
    </NotchedPanel>
  );
}

export default EvidenceGridModule;
